import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { ChevronDown, Menu, X } from 'lucide-react';
import Marquee from 'react-fast-marquee';

const TICKER_ITEMS = [
  'CELEBRATING 370+ WOMEN LEADERS',
  'EDITION 2 — APRIL 2026',
  '15 REGIONS, 7 CONTINENTS',
  '40 FREE COURSES AVAILABLE NOW',
  'NOMINATE A WOMAN WHO BUILT SOMETHING REAL',
  'THE FILM — COMING 2026',
];

const NAV_LINKS = [
  { label: 'Stories', to: '/profiles' },
  { label: 'The Film', to: '/film' },
  { label: 'Soundtrack', to: '/soundtrack' },
  { label: 'Podcast', to: '/podcast' },
  {
    label: 'The Book',
    children: [
      { label: 'Edition 1', to: '/book' },
      { label: 'Edition 2', to: '/book/edition-2' },
    ],
  },
  { label: 'Blog', to: '/blog' },
  { label: 'Community', to: '/community' },
  {
    label: 'Learn',
    children: [
      { label: 'Masterclasses', to: '/masterclasses' },
      { label: 'Courses', to: '/youth' },
      { label: 'Mentorship', to: '/mentorship' },
      { label: 'Leaderboard', to: '/leaderboard' },
    ],
  },
  {
    label: 'Connect',
    children: [
      { label: 'Events', to: '/events' },
      { label: 'Jobs', to: '/jobs' },
      { label: 'Benefits', to: '/benefits' },
      { label: 'Participate', to: '/participate' },
    ],
  },
];

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const location = useLocation();
  const navRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); setOpenDropdown(null); }, [location]);

  // Close dropdown on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) setOpenDropdown(null);
    };
    const onEsc = (e) => { if (e.key === 'Escape') setOpenDropdown(null); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, []);

  return (
    <>
      {/* NEWS TICKER */}
      <div className="fixed top-0 left-0 right-0 z-[60] bg-[#1A1A1A]" data-testid="news-ticker">
        <Marquee speed={40} gradient={false} className="py-2">
          {TICKER_ITEMS.map((item, i) => (
            <span key={i} className="mx-8 text-[11px] tracking-[0.2em] uppercase text-white/80" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {item} <span className="mx-6 text-[#C25E3E]">&bull;</span>
            </span>
          ))}
        </Marquee>
      </div>

      {/* MAIN NAV */}
      <header
        className={`fixed top-[36px] left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? 'backdrop-blur-2xl bg-[#FAF9F6]/85 border-b border-[#1A1A1A]/8 shadow-sm'
            : 'bg-transparent'
        }`}
        data-testid="main-header"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 flex items-center justify-between h-[72px]">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="header-logo">
            <span className="text-[24px] tracking-tight text-[#1A1A1A]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>
              She Built This
            </span>
          </Link>

          {/* Desktop nav */}
          <nav ref={navRef} className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {NAV_LINKS.map((link) =>
              link.children ? (
                <div
                  key={link.label}
                  className="relative"
                  onMouseEnter={() => setOpenDropdown(link.label)}
                  onMouseLeave={() => setOpenDropdown(null)}
                >
                  <button
                    type="button"
                    onClick={() => setOpenDropdown(openDropdown === link.label ? null : link.label)}
                    aria-expanded={openDropdown === link.label}
                    aria-haspopup="true"
                    className="flex items-center gap-1 text-[13px] tracking-wide text-[#4A4A4A] hover:text-[#C25E3E] transition-colors cursor-pointer"
                    style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 500 }}
                    data-testid={`${link.label.toLowerCase()}-nav-button`}
                  >
                    {link.label}
                    <ChevronDown size={14} className={`transition-transform duration-200 ${openDropdown === link.label ? 'rotate-180' : ''}`} />
                  </button>
                  {openDropdown === link.label && (
                    <div
                      className="absolute top-full left-0 pt-2 w-44 z-50"
                      data-testid={`${link.label.toLowerCase()}-dropdown`}
                    >
                      <div className="bg-white border border-[#1A1A1A]/10 shadow-lg py-2">
                        {link.children.map((child) => (
                          <Link
                            key={child.to}
                            to={child.to}
                            onClick={() => setOpenDropdown(null)}
                            className="block px-5 py-2.5 text-[13px] text-[#4A4A4A] hover:text-[#C25E3E] hover:bg-[#FAF9F6] transition-colors"
                            style={{ fontFamily: "'Outfit', sans-serif" }}
                            data-testid={`nav-${child.label.toLowerCase().replace(/ /g, '-')}`}
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Link key={link.to} to={link.to} className="text-[13px] tracking-wide text-[#4A4A4A] hover:text-[#C25E3E] transition-colors" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 500 }}>
                  {link.label}
                </Link>
              )
            )}
            <Link
              to="/nominate"
              className="ml-2 inline-flex items-center h-10 px-6 bg-[#C25E3E] text-white text-[12px] tracking-[0.15em] uppercase transition-all duration-300 hover:bg-[#A84D32]"
              style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}
              data-testid="nominate-cta"
            >
              Nominate
            </Link>
          </nav>

          {/* Mobile toggle */}
          <button className="lg:hidden p-2" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle">
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="lg:hidden bg-white border-t border-[#1A1A1A]/10 shadow-lg" data-testid="mobile-menu">
            <div className="px-6 py-6 space-y-1">
              {NAV_LINKS.map((link) =>
                link.children ? (
                  link.children.map((child) => (
                    <Link key={child.to} to={child.to} className="block py-3 text-[15px] text-[#4A4A4A] hover:text-[#C25E3E]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                      {child.label}
                    </Link>
                  ))
                ) : (
                  <Link key={link.to} to={link.to} className="block py-3 text-[15px] text-[#4A4A4A] hover:text-[#C25E3E]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {link.label}
                  </Link>
                )
              )}
              <Link to="/nominate" className="block mt-4 text-center py-3 bg-[#C25E3E] text-white text-sm tracking-wide uppercase" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>
                Nominate
              </Link>
            </div>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;
